//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/common/service';
import PondCultureModel from './pondCulture.model';
import FarmSiteModel from './farmSite.model';

/**
 * @service PondCultureService
 * @description Service for managing Pond Culture lifecycles and associated data within the aquaculture system.
 * A "Pond Culture" represents a specific crop cycle within a pond, from stocking to harvest.
 *
 * This service provides functionality to:
 * 1. **Cycle Management**: Retrieve active cultures and historical culture data for ponds.
 * 2. **Reporting**: Generate daily summaries, culture summaries, and traceability reports.
 * 3. **Data Aggregation**: Fetch consolidated data for farm sites to support dashboard views.
 *
 * The service interacts with the `/aqu/pondculture/` backend endpoint.
 *
 * **Key Responsibilities:**
 * - Fetching active cultures for specific farm sites.
 * - Retrieving detailed summaries for specific culture IDs.
 * - Generating traceability reports for compliance and auditing.
 * - Managing HTTP request lifecycles.
 *
 * @feature 1. Retrieve active pond cultures by farm site.
 * @feature 2. Generate detailed culture and pond summaries.
 * @feature 3. Fetch traceability reports for supply chain transparency.
 * @feature 4. Robust management of concurrent HTTP requests.
 *
 * @architecture
 * Extends base Service<PondCultureModel> providing REST API integration.
 * Uses HttpClient for HTTP operations and manages subscriptions for cleanup.
 *
 * @service-endpoint /aqu/pondculture/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class PondCultureService extends Service<PondCultureModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions.
   * Used to maintain references to active Observables so they can be unsubscribed
   * when the service is destroyed or when cancellation is requested.
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient.
   *
   * Initializes the service by calling the parent Service constructor with the
   * HttpClient instance and the specific API endpoint path (`/aqu/pondculture/`).
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests to the backend.
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/aqu/pondculture/');
  }

   /**
     * @method getAllPondCultures
     * @async
     * @description Fetch all Soil Type records from the backend.
     *
     * This method performs a GET request to the service endpoint. It is configured to retrieve
     * a large number of records (pageSize=2000) to ensure all types are loaded,
     * ordered by creation date ('new').
     *
     * @returns {Promise<Array<SoilTypeModel>>} Promise resolving to an array of SoilTypeModel objects.
     * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
     * @side-effect
     * - Initiates an HTTP GET request.
     * - Pushes the subscription to `ongoingRequests`.
     *
     * @example
     * soilTypeService.getAllSoilTypes().then(types => {
     *   console.log('Soil Types:', types);
     * });
     */
    public getAllPondCultures() {
      return new Promise<Array<PondCultureModel>>((resolve, reject) => {
        const request = this.httpClient.get<Array<PondCultureModel>>(`${this.baseUrl}${this.serviceUrl}?pageSize=2000&order=new`, this.httpOptions).subscribe(response => {
          resolve(response);
        }, (error: HttpErrorResponse) => {
          reject(error);
        });
        this.ongoingRequests.push(request);
      });
    }

  /**
   * @method getCurrentDatePondsByFarmSiteId
   * @async
   * @description Fetch active pond cultures for specific farm sites based on the current date.
   *
   * This is typically used to populate dashboards showing currently active ponds.
   *
   * @param {FarmSiteModel[]} farmSiteIds - Array of farm site models (containing IDs) to query.
   * @returns {Promise<Array<PondCultureModel>>} Promise resolving to an array of active PondCultureModel objects.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP POST request to `list/currentDate`.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public getCurrentDatePondsByFarmSiteId(farmSiteIds: FarmSiteModel[]) {
    return new Promise<Array<PondCultureModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<PondCultureModel>>(`${this.baseUrl}${this.serviceUrl}list/currentDate`, {
        farmSite: farmSiteIds
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getCultureSumaryByFarmSite
   * @async
   * @description Fetch a summary of a specific culture cycle.
   *
   * Retrieves aggregated metrics for a specific culture ID within a pond and farm site.
   *
   * @param {string} farmsiteId - The unique identifier of the farm site.
   * @param {number} pondId - The unique identifier of the pond.
   * @param {number} cultureId - The unique identifier of the culture cycle.
   * @returns {Promise<PondCultureModel>} Promise resolving to the culture summary details.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP POST request to `cultureSummaryDetails/farmSite`.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public getCultureSumaryByFarmSite(farmsiteId: string, pondId: number, cultureId: number) {
    return new Promise<PondCultureModel>((resolve, reject) => {
      const request = this.httpClient.post<PondCultureModel>(`${this.baseUrl}${this.serviceUrl}cultureSummaryDetails/farmSite`, {
        farmSite: [farmsiteId],
        pondId: pondId,
        pond_culture_id: cultureId
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getPondSumaryByFarmSite
   * @async
   * @description Fetch a summary of a specific pond's status.
   *
   * Similar to `getCultureSumaryByFarmSite`, but likely focuses on pond-level metrics rather than just the culture cycle.
   *
   * @param {string} farmsiteId - The unique identifier of the farm site.
   * @param {number} pondId - The unique identifier of the pond.
   * @param {number} cultureId - The unique identifier of the culture cycle.
   * @returns {Promise<PondCultureModel>} Promise resolving to the pond summary details.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP POST request to `pondSummaryDetails/farmSite`.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public getPondSumaryByFarmSite(farmsiteId: string, pondId: number, cultureId: number) {
    return new Promise<PondCultureModel>((resolve, reject) => {
      const request = this.httpClient.post<PondCultureModel>(`${this.baseUrl}${this.serviceUrl}pondSummaryDetails/farmSite`, {
        farmSite: [farmsiteId],
        pondId: pondId,
        pond_culture_id: cultureId
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getCulturesByPondId
   * @async
   * @description Fetch the history of culture cycles for a specific pond.
   *
   * @param {number} pondId - The unique identifier of the pond.
   * @returns {Promise<Array<PondCultureModel>>} Promise resolving to a list of culture cycles.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP GET request.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public getCulturesByPondId(pondId: number) {
    return new Promise<Array<PondCultureModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<PondCultureModel>>(`${this.baseUrl}${this.serviceUrl}pondId/${pondId}`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getDaySummaryReport
   * @async
   * @description Generate a daily summary report based on provided criteria.
   *
   * @param {any} pondCultureData - Filter object containing criteria for the report (e.g., date, site IDs).
   * @returns {Promise<Array<PondCultureModel>>} Promise resolving to the report data.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP POST request to `daySummary`.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public getDaySummaryReport(pondCultureData: any) {
    return new Promise<Array<PondCultureModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<PondCultureModel>>(`${this.baseUrl}${this.serviceUrl}daySummary`, pondCultureData, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getTraceabilityReport
   * @async
   * @description Generate a traceability report for a specific pond culture.
   *
   * Traceability reports are crucial for export compliance, tracking inputs (feed, chemicals) and outputs (harvest) for a specific crop.
   *
   * @param {string} businessname - Name of the business entity.
   * @param {string} siteName - Name of the farm site.
   * @param {string} sectionName - Name of the section.
   * @param {string} pondId - ID of the pond.
   * @param {number} cultureId - ID of the culture cycle.
   * @returns {Promise<PondCultureModel>} Promise resolving to the traceability report data.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP POST request to `pondTraceability`.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public getTraceabilityReport(businessname: string, siteName: string, sectionName: string, pondId: string, cultureId: number) {
    return new Promise<PondCultureModel>((resolve, reject) => {
      const request = this.httpClient.post<PondCultureModel>(`${this.baseUrl}${this.serviceUrl}pondTraceability`, {
        businessName: businessname,
        farmSiteName: siteName,
        sectionName: sectionName,
        pond_id: Number(pondId),
        pond_culture_id: cultureId
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }
  
  //To Cancel the onGoing Http Requests
  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks.
   *
   * This method iterates through the `ongoingRequests` array and calls `unsubscribe()`
   * on each subscription. This is typically called when a component using this service
   * is destroyed (ngOnDestroy) to ensure that no network requests continue in the background.
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests tracked in `ongoingRequests`.
   * - Resets `ongoingRequests` to an empty array.
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
